import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Calendar } from "lucide-react";

const FALLBACK_POSTS = [
  {
    id: "fb1",
    slug: "why-your-marketing-site-is-underperforming",
    title: "Why your marketing site is underperforming (and what to fix first)",
    excerpt: "Most sites lose visitors in the first 4 seconds. Here's a diagnostic framework we use with every new client.",
    cover_image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop",
    tags: ["Marketing"],
    author: "Sarah Chen",
    created_at: new Date().toISOString(),
  },
  {
    id: "fb2",
    slug: "the-design-system-playbook",
    title: "The design system playbook for small teams",
    excerpt: "You don't need a 200-page Figma library. Here's the minimum viable design system to move fast.",
    cover_image: "https://images.unsplash.com/photo-1587440871875-191322ee64b0?w=1200&h=800&fit=crop",
    tags: ["Design"],
    author: "Amelia Patel",
    created_at: new Date().toISOString(),
  },
  {
    id: "fb3",
    slug: "ecommerce-checkout-optimization",
    title: "9 checkout optimizations that lifted conversion 22%",
    excerpt: "Real experiments, real numbers. What worked, what didn't, and what surprised us.",
    cover_image: "https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=1200&h=800&fit=crop",
    tags: ["E-commerce"],
    author: "Sofia Ramirez",
    created_at: new Date().toISOString(),
  },
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data.length ? r.data : FALLBACK_POSTS))
      .catch(() => setPosts(FALLBACK_POSTS))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div>
      <section className="border-b border-brand-border">
        <div className="container-x pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">The Journal</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight" data-testid="blog-title">Field notes on design, growth & craft.</h1>
            <p className="mt-6 text-lg text-brand-muted">Practical, unhyped writing from the TelstraNics team.</p>
          </div>
        </div>
      </section>

      <section className="container-x py-16 lg:py-20">
        {loading ? (
          <div className="text-center py-20 text-brand-muted">Loading posts...</div>
        ) : (
          <>
            {featured && (
              <Link to={`/blog/${featured.slug}`} data-testid="blog-featured" className="group grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
                <div className="lg:col-span-7 aspect-[16/10] overflow-hidden rounded-3xl bg-brand-surface">
                  <img src={featured.cover_image || "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200"} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-3 text-xs text-brand-muted"><Calendar className="w-3.5 h-3.5" /> {new Date(featured.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</div>
                  <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-brand-ink group-hover:text-brand-blue transition-colors">{featured.title}</h2>
                  <p className="mt-4 text-brand-muted leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand-ink">Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </Link>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {rest.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group">
                  <div className="aspect-[16/10] overflow-hidden rounded-2xl bg-brand-surface mb-4">
                    <img src={p.cover_image || "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800"} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="text-xs text-brand-muted mb-2 flex items-center gap-2">
                    {p.tags?.[0] && <span className="px-2 py-0.5 bg-brand-surface rounded-full">{p.tags[0]}</span>}
                    <span>{new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  </div>
                  <h3 className="font-display font-bold text-xl text-brand-ink group-hover:text-brand-blue transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-brand-muted line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
